// FieldMeasurementSection.js
// This component handles the Field Measurements section of the Wells tab

import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
// Adding the appStore
import { useAppStore } from "../../../../../store/appStore";
// Constants
import {
  EXTRACTED_VOLUME_UNIT_OPTIONS,
  SPECIFIC_CONDUCTANCE_UNIT_OPTIONS,
} from "../../../../../constants/samplingEventConstants";
import { SamplingEventDatePicker } from "../../../common/SamplingEventDatePicker";
import { SamplingEventTimePickerInput } from "../../../common/SamplingEventTimePickerInput";
import { ConfirmationModal } from "../../../common/ConfirmationModal";
import styles from "./FieldMeasurementsSection.module.css";

// Sampling Start Modal Component
const SamplingStartModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className={styles.confirmationModalOverlay}>
      <div className={styles.confirmationModalContent}>
        <h3 className={styles.samplingStartModalHeader}>
          Sampling Notification
        </h3>
        <p className={styles.samplingStartModalMessage}>
          Field measurements have stabilized. Sampling can now begin.
        </p>
        <button className={styles.samplingStartModalButton} onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

// Field Measurement Tooltip Component, a button the user clicks to display a tooltip with information about the field measurement stabilization criteria
const FieldMeasurementTooltip = () => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);
  const buttonRef = useRef(null);
  const containerRef = useRef(null);

  // Handle escape key press for tooltip
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === "Escape" && showTooltip) {
        setShowTooltip(false);
      }
    };

    const handleGlobalClick = (e) => {
      // Check if click is outside both the tooltip and the button
      if (
        showTooltip &&
        tooltipRef.current &&
        buttonRef.current &&
        !tooltipRef.current.contains(e.target) &&
        !buttonRef.current.contains(e.target)
      ) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener("keydown", handleEscapeKey);
      document.addEventListener("click", handleGlobalClick);

      // Adjust tooltip position if it goes off-screen
      if (tooltipRef.current && containerRef.current) {
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        if (tooltipRect.right > viewportWidth) {
          tooltipRef.current.style.left = "auto";
          tooltipRef.current.style.right = "0";
        }
      }
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
      document.removeEventListener("click", handleGlobalClick);
    };
  }, [showTooltip]);

  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowTooltip(!showTooltip);
  };

  return (
    <div className={styles.fieldMeasurementTooltipContainer} ref={containerRef}>
      <button
        ref={buttonRef}
        className={styles.fieldMeasurementTooltipButton}
        onClick={handleButtonClick}
        aria-label="Field measurement stabilization criteria"
        type="button"
      >
        Sample Params
      </button>

      {showTooltip && (
        <div
          ref={tooltipRef}
          role="tooltip"
          className={styles.fieldMeasurementTooltipContent}
        >
          <button
            className={styles.tooltipCloseButton}
            onClick={() => setShowTooltip(false)}
            aria-label="Close tooltip"
          >
            ×
          </button>
          <h4>Sampling Criteria:</h4>
          <ul className={styles.stabilizationList}>
            <li>DTW: &lt;0.33 feet of previous 2 measurements</li>
            <li>Temperature: ±0.1 of previous 2 measurements</li>
            <li>pH: ±0.1 of previous 2 measurements</li>
            <li>Specific Conductance: ±3% of previous 2 measurements</li>
            <li>ORP: ±3% of previous 2 measurements</li>
            <li>DO: ±10% of previous 2 measurements</li>
            <li>Turbidity: ±10% or &lt;10 NTU of previous 2 measurements</li>
          </ul>
          <p className={styles.tooltipNote}>
            At least 3 consecutive measurements must meet all criteria to begin
            sampling.
          </p>
        </div>
      )}
    </div>
  );
};

// Field Measurement Modal Component - Uses tempState pattern
const FieldMeasurementModal = ({
  well,
  existingMeasurement,
  isAddMode,
  onClose,
  onSave,
  wellsActions,
}) => {
  // Initialize tempState using the action helper
  const [tempState, setTempState] = useState(() =>
    wellsActions.createInitialFieldMeasurementTempState(
      isAddMode ? null : existingMeasurement,
      well.id,
      well
    )
  );

  // Ref to prevent double-saves
  const isSavingRef = useRef(false);

  // Helper to update tempState data fields
  const updateTempData = (field, value) => {
    setTempState((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        [field]: value,
      },
    }));
  };

  // Handle numeric input changes with validation
  const handleNumericInput = (field, value) => {
    if (value === "" || value === null || value === undefined) {
      updateTempData(field, null);
      return;
    }

    // For ORP, allow negative numbers
    if (field === "orp") {
      // Only process numeric input with optional negative sign
      if (!/^-?\d*\.?\d*$/.test(value)) {
        return;
      }
    } else {
      // For other fields, only allow positive numbers
      if (!/^\d*\.?\d*$/.test(value)) {
        return;
      }
    }

    updateTempData(field, value);
  };

  // Format to 2 decimal places when focus is lost
  const handleNumericBlur = (e, field) => {
    const { value } = e.target;
    if (value) {
      // Parse as float and round to 2 decimal places
      const formattedValue = parseFloat(value).toFixed(2);
      updateTempData(field, formattedValue);
    } else {
      updateTempData(field, null);
    }
  };

  // Handle select change
  const handleSelectChange = (field, selected) => {
    updateTempData(field, selected?.value || null);
  };

  // Handle auto-fill button for date and time
  const handleAutoFillDateTime = () => {
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
    const currentTime = currentDate.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    setTempState((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        measurementDate: formattedDate,
        measurementTime: currentTime,
      },
    }));
  };

  // Handle date change from DatePicker
  const handleDateChange = (date) => {
    if (!date) {
      updateTempData("measurementDate", null);
      return;
    }
    const formattedDate = date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
    updateTempData("measurementDate", formattedDate);
  };

  // Handle time change
  const handleTimeChange = (time) => {
    updateTempData("measurementTime", time || null);
  };

  // Handle save - pass tempState to parent
  const handleSave = () => {
    if (isSavingRef.current) {
      return;
    }
    isSavingRef.current = true;
    onSave(tempState);
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3>{isAddMode ? "Add Measurement" : "Edit Measurement"}</h3>
          <button className={styles.closeButton} onClick={onClose}>
            x
          </button>
        </div>
        <div className={styles.modalBody}>
          <button
            className={styles.dateTimeAutoFillButton}
            onClick={handleAutoFillDateTime}
          >
            Auto-Fill Date/Time
          </button>
          {/* Date */}
          <div className={styles.modalField}>
            <label className={styles.modalLabel} htmlFor="measurementDate">
              Date
            </label>
            <SamplingEventDatePicker
              selected={tempState.data.measurementDate}
              onChange={handleDateChange}
              id="measurementDate"
              dateFormat="MM/dd/yyyy"
              className={styles.modalInput}
              placeholderText="Select Date"
              popperClassName={styles.datePickerPopper}
              shouldCloseOnSelect={true}
              showMonthDropdown
              showYearDropdown
              scrollableYearDropdown
            />
          </div>

          {/* Time */}
          <div className={styles.modalField}>
            <label className={styles.modalLabel} htmlFor="measurementTime">
              Time
            </label>
            <SamplingEventTimePickerInput
              value={tempState.data.measurementTime || ""}
              onChange={handleTimeChange}
              id="measurementTime"
            />
          </div>

          {/* Extracted Volume */}
          <div className={styles.modalField}>
            <label className={styles.modalLabel} htmlFor="extractedVolume">
              Extracted Volume
            </label>
            <input
              type="text"
              id="extractedVolume"
              className={styles.modalInput}
              value={tempState.data.extractedVolume || ""}
              onChange={(e) =>
                handleNumericInput("extractedVolume", e.target.value)
              }
              onBlur={(e) => handleNumericBlur(e, "extractedVolume")}
              placeholder="0.00"
            />
          </div>

          {/* Extracted Volume Unit */}
          <div className={styles.modalField}>
            <label className={styles.modalLabel} htmlFor="extractedVolumeUnit">
              Extracted Volume Unit
            </label>
            <Select
              id="extractedVolumeUnit"
              value={
                tempState.data.extractedVolumeUnit
                  ? {
                      label: tempState.data.extractedVolumeUnit,
                      value: tempState.data.extractedVolumeUnit,
                    }
                  : null
              }
              onChange={(selected) =>
                handleSelectChange("extractedVolumeUnit", selected)
              }
              options={EXTRACTED_VOLUME_UNIT_OPTIONS}
              className={styles.modalInput}
              isClearable
            />
          </div>
          {/* DTW */}
          <div className={styles.modalField}>
            <label className={styles.modalLabel} htmlFor="dtw">
              DTW (ft)
            </label>
            <input
              type="text"
              id="dtw"
              className={styles.modalInput}
              value={tempState.data.dtw || ""}
              onChange={(e) => handleNumericInput("dtw", e.target.value)}
              onBlur={(e) => handleNumericBlur(e, "dtw")}
              placeholder="0.00"
            />
          </div>

          {/* Temperature */}
          <div className={styles.modalField}>
            <label className={styles.modalLabel} htmlFor="temperature">
              Temperature (C)
            </label>
            <input
              type="text"
              id="temperature"
              className={styles.modalInput}
              value={tempState.data.temperature || ""}
              onChange={(e) =>
                handleNumericInput("temperature", e.target.value)
              }
              onBlur={(e) => handleNumericBlur(e, "temperature")}
              placeholder="0.00"
            />
          </div>

          {/* pH */}
          <div className={styles.modalField}>
            <label className={styles.modalLabel} htmlFor="ph">
              pH (SU)
            </label>
            <input
              type="text"
              id="ph"
              className={styles.modalInput}
              value={tempState.data.ph || ""}
              onChange={(e) => handleNumericInput("ph", e.target.value)}
              onBlur={(e) => handleNumericBlur(e, "ph")}
              placeholder="0.00"
            />
          </div>

          {/* Specific Conductance */}
          <div className={styles.modalField}>
            <label className={styles.modalLabel} htmlFor="specificConductance">
              Specific Conductance
            </label>
            <input
              type="text"
              id="specificConductance"
              className={styles.modalInput}
              value={tempState.data.specificConductance || ""}
              onChange={(e) =>
                handleNumericInput("specificConductance", e.target.value)
              }
              onBlur={(e) => handleNumericBlur(e, "specificConductance")}
              placeholder="0.00"
            />
          </div>

          {/* Specific Conductance Unit */}
          <div className={styles.modalField}>
            <label
              className={styles.modalLabel}
              htmlFor="specificConductanceUnit"
            >
              Specific Conductance Unit
            </label>
            <Select
              id="specificConductanceUnit"
              value={
                tempState.data.specificConductanceUnit
                  ? {
                      label: tempState.data.specificConductanceUnit,
                      value: tempState.data.specificConductanceUnit,
                    }
                  : null
              }
              onChange={(selected) =>
                handleSelectChange("specificConductanceUnit", selected)
              }
              options={SPECIFIC_CONDUCTANCE_UNIT_OPTIONS}
              className={styles.modalInput}
              isClearable
            />
          </div>
          {/* ORP */}
          <div className={styles.modalField}>
            <label className={styles.modalLabel} htmlFor="orp">
              ORP (mV)
            </label>
            <input
              type="text"
              id="orp"
              className={styles.modalInput}
              value={tempState.data.orp || ""}
              onChange={(e) => handleNumericInput("orp", e.target.value)}
              onBlur={(e) => handleNumericBlur(e, "orp")}
              placeholder="0.00"
            />
          </div>

          {/* DO */}
          <div className={styles.modalField}>
            <label className={styles.modalLabel} htmlFor="doMeasurement">
              DO (mg/L)
            </label>
            <input
              type="text"
              id="doMeasurement"
              className={styles.modalInput}
              value={tempState.data.doMeasurement || ""}
              onChange={(e) =>
                handleNumericInput("doMeasurement", e.target.value)
              }
              onBlur={(e) => handleNumericBlur(e, "doMeasurement")}
              placeholder="0.00"
            />
          </div>

          {/* Turbidity */}
          <div className={styles.modalField}>
            <label className={styles.modalLabel} htmlFor="turbidity">
              Turbidity (nTU)
            </label>
            <input
              type="text"
              id="turbidity"
              className={styles.modalInput}
              value={tempState.data.turbidity || ""}
              onChange={(e) => handleNumericInput("turbidity", e.target.value)}
              onBlur={(e) => handleNumericBlur(e, "turbidity")}
              placeholder="0.00"
            />
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button className={styles.saveButton} onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Field Measurement Section Component
export const FieldMeasurementSection = ({
  well,
  ui,
  wellsActions,
  emitPatch,
}) => {
  // Notification modal
  const showNotificationModal = useAppStore(
    (s) => s.notificationActions.showNotification
  );
  // Modal state now comes from store
  const isFieldMeasurementModalOpen = ui?.isFieldMeasurementModalOpen;
  const isAddMode = ui?.isFieldMeasurementAddMode;

  // Delete confirmation modal
  const isFieldMeasurementConfirmationModalOpen =
    ui?.isFieldMeasurementConfirmationModalOpen;

  // State for sampling start modal (kept local)
  const [showSamplingStartModal, setShowSamplingStartModal] = useState(false);

  // State for the current measurement that is being edited or deleted
  const [currentMeasurement, setCurrentMeasurement] = useState(null);

  // Helper to compute a comparable timestamp from measurement date/time (ascending -> newest at bottom)
  const getMeasurementTimestamp = (m) => {
    try {
      const dateStr = m?.measurementDate || null; // expected MM/DD/YYYY
      const timeStr = m?.measurementTime || "00:00"; // HH:MM
      if (!dateStr) return 0; // Place unscheduled at top
      const [mm, dd, yyyy] = String(dateStr).split("/");
      if (!yyyy || !mm || !dd) return 0;
      const iso = `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(
        2,
        "0"
      )}T${String(timeStr).padStart(5, "0")}:00`;
      const ts = new Date(iso).getTime();
      return isNaN(ts) ? 0 : ts;
    } catch {
      return 0;
    }
  };

  // Get field measurements from the well, ensuring they're sorted by date/time (ascending)
  const fieldMeasurements = [...(well.fieldMeasurements || [])].sort((a, b) => {
    const ta = getMeasurementTimestamp(a);
    const tb = getMeasurementTimestamp(b);
    if (ta !== tb) return ta - tb;
    // tie-breaker: sortOrder if present
    const sa =
      typeof a.sortOrder === "number" && !isNaN(a.sortOrder)
        ? a.sortOrder
        : Infinity;
    const sb =
      typeof b.sortOrder === "number" && !isNaN(b.sortOrder)
        ? b.sortOrder
        : Infinity;
    if (sa !== sb) return sa - sb;
    // final tie-breaker: stable by rowId
    return String(a.rowId).localeCompare(String(b.rowId));
  });

  // Open modal for adding a new measurement - no longer creates measurement immediately
  const handleAddMeasurement = () => {
    setCurrentMeasurement(null); // Clear any existing measurement (add mode)
    wellsActions.openFieldMeasurementModal(true); // true = isAddMode
  };

  // Open modal for editing an existing measurement
  const handleEditMeasurement = (measurement) => {
    setCurrentMeasurement(measurement);
    wellsActions.openFieldMeasurementModal(false); // false = not add mode (edit mode)
  };

  // Close the modal - discards any temporary changes
  const handleCloseFieldMeasurementModal = () => {
    wellsActions.closeFieldMeasurementModal();
    setCurrentMeasurement(null);
  };

  // Save measurement from modal - commits tempState to data model and server
  const handleSaveMeasurement = async (tempState) => {
    const result = await wellsActions.commitTempFieldMeasurement(
      tempState,
      emitPatch
    );

    if (result.ok) {
      showNotificationModal({
        type: "success",
        title: "Measurement Saved",
        message: "The field measurement has been saved successfully.",
      });
    } else {
      showNotificationModal({
        type: "error",
        title: "Error Saving Measurement",
        message:
          result.error || "An error occurred while saving the measurement.",
      });
    }

    // Always close modal after save attempt
    wellsActions.closeFieldMeasurementModal();
    setCurrentMeasurement(null);
  };

  // Initiate delete process for a measurement
  const openDeleteConfirmationModal = (measurement) => {
    setCurrentMeasurement(measurement);
    wellsActions.openFieldMeasurementConfirmationModal();
  };

  // Cancel deletion
  const handleCloseDeleteConfirmationModal = () => {
    wellsActions.closeFieldMeasurementConfirmationModal();
    setCurrentMeasurement(null);
  };

  // Confirm deletion
  const handleConfirmDelete = () => {
    if (currentMeasurement) {
      wellsActions.removeFieldMeasurement(
        well.id,
        currentMeasurement.rowId,
        emitPatch
      );
      wellsActions.closeFieldMeasurementConfirmationModal();
      setCurrentMeasurement(null);
    }
  };

  // Format time for display
  const formatTime = (time) => {
    if (!time) return "-";
    return time;
  };

  // Render measurement cards for mobile and tablet view
  const renderMeasurementCards = () => {
    if (fieldMeasurements.length === 0) {
      return (
        <div className={styles.noMeasurements}>
          No field measurements recorded. Click "Add Measurement" to add one.
        </div>
      );
    }

    return (
      <div className={styles.measurementsCardContainer}>
        {fieldMeasurements.map((measurement) => (
          <div key={measurement.rowId} className={styles.measurementCard}>
            <div className={styles.measurementCardHeader}>
              <span className={styles.measurementDate}>
                {measurement.measurementDate || "-"}
              </span>
              <span className={styles.measurementTime}>
                {formatTime(measurement.measurementTime)}
              </span>
            </div>
            <div className={styles.measurementCardContent}>
              <div className={styles.measurementField}>
                <span className={styles.measurementLabel}>
                  {/* Extracted Volume (gal) */}
                  EV
                </span>
                <span className={styles.measurementValue}>
                  {measurement.extractedVolume || "-"}
                </span>
              </div>
              <div className={styles.measurementField}>
                <span className={styles.measurementLabel}>
                  {/* Extracted Volume Unit */}
                  EV Unit
                </span>
                <span className={styles.measurementValue}>
                  {measurement.extractedVolumeUnit || "-"}
                </span>
              </div>
              <div className={styles.measurementField}>
                <span className={styles.measurementLabel}>DTW (ft)</span>
                <span className={styles.measurementValue}>
                  {measurement.dtw || "-"}
                </span>
              </div>
              <div className={styles.measurementField}>
                <span className={styles.measurementLabel}>Temp (°C)</span>
                <span className={styles.measurementValue}>
                  {measurement.temperature || "-"}
                </span>
              </div>
              <div className={styles.measurementField}>
                <span className={styles.measurementLabel}>pH (SU)</span>
                <span className={styles.measurementValue}>
                  {measurement.ph || "-"}
                </span>
              </div>
              <div className={styles.measurementField}>
                <span className={styles.measurementLabel}>
                  {/* Specific Conductance */}
                  Spec Con
                </span>
                <span className={styles.measurementValue}>
                  {measurement.specificConductance || "-"}
                </span>
              </div>
              {/* Specific Conductance measurement */}
              <div className={styles.measurementField}>
                <span className={styles.measurementLabel}>SC Unit</span>
                <span className={styles.measurementValue}>
                  {measurement.specificConductanceUnit}
                </span>
              </div>
              <div className={styles.measurementField}>
                <span className={styles.measurementLabel}>ORP (mV)</span>
                <span className={styles.measurementValue}>
                  {measurement.orp || "-"}
                </span>
              </div>
              <div className={styles.measurementField}>
                <span className={styles.measurementLabel}>DO (mg/L)</span>
                <span className={styles.measurementValue}>
                  {measurement.doMeasurement || "-"}
                </span>
              </div>
              <div className={styles.measurementField}>
                <span className={styles.measurementLabel}>
                  {/* Turbidity */}
                  Turb (nTU)
                </span>
                <span className={styles.measurementValue}>
                  {measurement.turbidity || "-"}
                </span>
              </div>
            </div>
            <div className={styles.measurementActions}>
              <button
                className={styles.editButton}
                onClick={() => handleEditMeasurement(measurement)}
              >
                Edit
              </button>
              <button
                className={styles.deleteButton}
                onClick={() => openDeleteConfirmationModal(measurement)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render measurement table for desktop view
  const renderMeasurementTable = () => {
    if (fieldMeasurements.length === 0) {
      return (
        <div className={styles.noMeasurements}>
          No field measurements recorded. Click "Add Measurement" to add one.
        </div>
      );
    }

    return (
      <div className={styles.measurementsTableContainer}>
        <table className={styles.measurementsTable}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Time</th>
              <th>Extrac Vol</th>
              <th>EV Unit</th>
              <th>DTW (ft)</th>
              <th>Temp (°C)</th>
              <th>pH (SU)</th>
              <th>Spec Con</th>
              <th>SC Unit</th>
              <th>ORP (mV)</th>
              <th>DO (mg/L)</th>
              <th>Turb (nTU)</th>
              <th className={styles.actionsColumn}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {fieldMeasurements.map((measurement) => (
              <tr key={measurement.rowId}>
                <td>{measurement.measurementDate || "-"}</td>
                <td>{formatTime(measurement.measurementTime)}</td>
                <td>{measurement.extractedVolume || "-"}</td>
                <td>{measurement.extractedVolumeUnit || "-"}</td>
                <td>{measurement.dtw || "-"}</td>
                <td>{measurement.temperature || "-"}</td>
                <td>{measurement.ph || "-"}</td>
                <td>{measurement.specificConductance || "-"}</td>
                <td>{measurement.specificConductanceUnit || "-"}</td>
                <td>{measurement.orp || "-"}</td>
                <td>{measurement.doMeasurement || "-"}</td>
                <td>{measurement.turbidity || "-"}</td>
                <td className={styles.actionsColumn}>
                  <div className={styles.measurementActions}>
                    <button
                      className={styles.editButton}
                      onClick={() => handleEditMeasurement(measurement)}
                    >
                      Edit
                    </button>
                    <button
                      className={styles.deleteButton}
                      onClick={() => openDeleteConfirmationModal(measurement)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Calculation to show when the sampling can begin if the low flow checkbox is checked. Other items that need to be true to for stabilization to occur:
  // 1. At least 3 field measurements need to be present that contain the same date. The first field measurement is used as the baseline for which the other field measurements are compared.
  // 2. DTW needs to be stable and within <0.33 feet of the previous field measurement.
  // 3. Temperature needs to be +- 0.1 of the previous field measurement.
  // 4. ph needs to be +- 0.1 of the previous field measurement.
  // 5. Specific Conductance needs to be +- 3% of the previous field measurement.
  // 6. ORP needs to be +- 3% of the previous field measurement.
  // 7. DO needs to be +- 10% of the previous field measurement.
  // 8. Turbidity needs to be +- 10% or <10 NTU of the previous field measurement.

  const calculateSamplingStart = (
    fieldMeasurementOne,
    fieldMeasurementTwo,
    fieldMeasurementThree
  ) => {
    // Coerce to numeric where needed
    const n = (v) =>
      v === null || v === undefined || v === "" ? NaN : Number(v);
    const fm1 = {
      dtw: n(fieldMeasurementOne.dtw),
      temperature: n(fieldMeasurementOne.temperature),
      ph: n(fieldMeasurementOne.ph),
      specificConductance: n(fieldMeasurementOne.specificConductance),
      orp: n(fieldMeasurementOne.orp),
      doMeasurement: n(fieldMeasurementOne.doMeasurement),
      turbidity: n(fieldMeasurementOne.turbidity),
    };
    const fm2 = {
      dtw: n(fieldMeasurementTwo.dtw),
      temperature: n(fieldMeasurementTwo.temperature),
      ph: n(fieldMeasurementTwo.ph),
      specificConductance: n(fieldMeasurementTwo.specificConductance),
      orp: n(fieldMeasurementTwo.orp),
      doMeasurement: n(fieldMeasurementTwo.doMeasurement),
      turbidity: n(fieldMeasurementTwo.turbidity),
    };
    const fm3 = {
      dtw: n(fieldMeasurementThree.dtw),
      temperature: n(fieldMeasurementThree.temperature),
      ph: n(fieldMeasurementThree.ph),
      specificConductance: n(fieldMeasurementThree.specificConductance),
      orp: n(fieldMeasurementThree.orp),
      doMeasurement: n(fieldMeasurementThree.doMeasurement),
      turbidity: n(fieldMeasurementThree.turbidity),
    };
    // Check if DTW is stable and within <0.33' of the first field measurement
    if (isNaN(fm1.dtw) || isNaN(fm2.dtw) || isNaN(fm3.dtw)) return false;
    if (fm1.dtw > fm2.dtw + 0.33 || fm1.dtw < fm2.dtw - 0.33) {
      console.log(
        "DTW is not stable and within <0.33' of the first field measurement"
      );
      return false;
    }
    if (fm1.dtw > fm3.dtw + 0.33 || fm1.dtw < fm3.dtw - 0.33) {
      console.log(
        "DTW is not stable and within <0.33' of the first field measurement"
      );
      return false;
    }
    // Check if temperature is +- 0.1 of the previous field measurement
    if (
      isNaN(fm1.temperature) ||
      isNaN(fm2.temperature) ||
      isNaN(fm3.temperature)
    )
      return false;
    if (
      fm1.temperature > fm2.temperature + 0.1 ||
      fm1.temperature < fm2.temperature - 0.1
    ) {
      console.log(
        "Temperature is not +- 0.1 of the previous field measurement"
      );
      return false;
    }
    if (
      fm1.temperature > fm3.temperature + 0.1 ||
      fm1.temperature < fm3.temperature - 0.1
    ) {
      console.log("Temperature is not +- 0.1 of the first field measurement");
      return false;
    }
    // Check if ph is +- 0.1 of the previous field measurement
    if (isNaN(fm1.ph) || isNaN(fm2.ph) || isNaN(fm3.ph)) return false;
    if (fm1.ph > fm2.ph + 0.1 || fm1.ph < fm2.ph - 0.1) {
      console.log("PH is not +- 0.1 of the previous field measurement");
      return false;
    }
    if (fm1.ph > fm3.ph + 0.1 || fm1.ph < fm3.ph - 0.1) {
      console.log("PH is not +- 0.1 of the first field measurement");
      return false;
    }
    // Check if specific conductance is +- 3% of the previous field measurement
    if (
      isNaN(fm1.specificConductance) ||
      isNaN(fm2.specificConductance) ||
      isNaN(fm3.specificConductance)
    )
      return false;
    if (
      fm1.specificConductance > fm2.specificConductance * 1.03 ||
      fm1.specificConductance < fm2.specificConductance * 0.97
    ) {
      console.log(
        "Specific Conductance is not +- 3% of the previous field measurement"
      );
      return false;
    }
    if (
      fm1.specificConductance > fm3.specificConductance * 1.03 ||
      fm1.specificConductance < fm3.specificConductance * 0.97
    ) {
      console.log(
        "Specific Conductance is not +- 3% of the first field measurement"
      );
      return false;
    }
    // Check if ORP is +- 3% of the previous field measurement
    if (isNaN(fm1.orp) || isNaN(fm2.orp) || isNaN(fm3.orp)) return false;
    if (fm1.orp > fm2.orp * 1.03 || fm1.orp < fm2.orp * 0.97) {
      console.log("ORP is not +- 3% of the previous field measurement");
      return false;
    }
    if (fm1.orp > fm3.orp * 1.03 || fm1.orp < fm3.orp * 0.97) {
      console.log("ORP is not +- 3% of the first field measurement");
      return false;
    }
    // Check if DO is +- 10% of the previous field measurement
    if (
      isNaN(fm1.doMeasurement) ||
      isNaN(fm2.doMeasurement) ||
      isNaN(fm3.doMeasurement)
    )
      return false;
    if (
      fm1.doMeasurement > fm2.doMeasurement * 1.1 ||
      fm1.doMeasurement < fm2.doMeasurement * 0.9
    ) {
      console.log("DO is not +- 10% of the previous field measurement");
      return false;
    }
    if (
      fm1.doMeasurement > fm3.doMeasurement * 1.1 ||
      fm1.doMeasurement < fm3.doMeasurement * 0.9
    ) {
      console.log("DO is not +- 10% of the first field measurement");
      return false;
    }
    // Check if Turbidity is within ±10% OR < 10 NTU
    if (isNaN(fm1.turbidity) || isNaN(fm2.turbidity) || isNaN(fm3.turbidity))
      return false;
    const turbidityWithin = (baseline, reading) =>
      reading < 10 || (reading >= baseline * 0.9 && reading <= baseline * 1.1);
    if (!turbidityWithin(fm2.turbidity, fm1.turbidity)) return false;
    if (!turbidityWithin(fm3.turbidity, fm1.turbidity)) return false;
    return true;
  };

  // useEffect to calculate when the sampling can begin if the low flow checkbox is checked and at least 3 field measurements are present that have the same date and respond to any changes in the well.fieldMeasurements, including the addition or removal of field measurements, and changes within the field measurements.
  useEffect(() => {
    console.log("well.fieldMeasurements", well.fieldMeasurements);
    // Ensure well.fieldMeasurements is an array and has at least two items
    if (
      well.lowFlow &&
      Array.isArray(well.fieldMeasurements) &&
      well.fieldMeasurements.length >= 3 &&
      well.sampleTime === ""
    ) {
      // Create a copy before sorting to avoid mutating the prop and ensure stable sort order handling
      const sortedFieldMeasurements = [...well.fieldMeasurements].sort(
        (a, b) => {
          const ta = getMeasurementTimestamp(a);
          const tb = getMeasurementTimestamp(b);
          if (ta !== tb) return ta - tb;
          const sa =
            typeof a.sortOrder === "number" && !isNaN(a.sortOrder)
              ? a.sortOrder
              : Infinity;
          const sb =
            typeof b.sortOrder === "number" && !isNaN(b.sortOrder)
              ? b.sortOrder
              : Infinity;
          if (sa !== sb) return sa - sb;
          return String(a.rowId).localeCompare(String(b.rowId));
        }
      );

      // Get last 2 field measurements after sorting
      const lastMeasurement =
        sortedFieldMeasurements[sortedFieldMeasurements.length - 1];
      const secondLastMeasurement =
        sortedFieldMeasurements[sortedFieldMeasurements.length - 2];
      const thirdLastMeasurement =
        sortedFieldMeasurements[sortedFieldMeasurements.length - 3];

      // Ensure both measurements and their dates are valid before comparison
      if (
        lastMeasurement &&
        lastMeasurement.measurementDate &&
        secondLastMeasurement &&
        secondLastMeasurement.measurementDate
      ) {
        if (
          lastMeasurement.measurementDate ===
            secondLastMeasurement.measurementDate &&
          lastMeasurement.measurementDate ===
            thirdLastMeasurement.measurementDate
        ) {
          console.log(
            "Last measurement date is the same as the second last measurement date"
          );
          // Check if the sampling can begin
          if (
            calculateSamplingStart(
              secondLastMeasurement,
              thirdLastMeasurement,
              lastMeasurement
            )
          ) {
            // Display a modal to the user that the sampling can begin
            setShowSamplingStartModal(true);
          }
        }
      }
    } else {
      // If conditions for showing the modal aren't met (e.g., not low flow, or not enough measurements), ensure the modal is hidden.
      console.log("Not enough measurements to show sampling start modal");
      setShowSamplingStartModal(false);
    }
  }, [
    well.lowFlow,
    // Stringify relevant parts of fieldMeasurements to ensure useEffect triggers on deep changes
    JSON.stringify(
      (well.fieldMeasurements || []).map((fm) => ({
        rowId: fm.rowId, // Unique key for the measurement
        dtw: fm.dtw,
        temperature: fm.temperature,
        ph: fm.ph,
        specificConductance: fm.specificConductance,
        orp: fm.orp,
        doMeasurement: fm.doMeasurement,
        turbidity: fm.turbidity,
        measurementDate: fm.measurementDate, // Used for date comparison
        sortOrder: fm.sortOrder, // Used for sorting
      }))
    ),
  ]);

  return (
    <div className={styles.fieldMeasurementsSection}>
      <div className={styles.sectionHeader}>
        <div className={styles.headerTitleContainer}>
          <h4>4. Field Measurements</h4>
        </div>
        <div className={styles.headerButtonsContainer}>
          <FieldMeasurementTooltip />
          <button
            className={styles.addMeasurementButton}
            onClick={handleAddMeasurement}
          >
            Add Measurement
          </button>
        </div>
      </div>
      {/* Card view (mobile and tablet) and Table view (desktop) */}
      {renderMeasurementCards()}
      {renderMeasurementTable()}

      {/* Modal for adding/editing measurements */}
      {isFieldMeasurementModalOpen && (
        <FieldMeasurementModal
          well={well}
          existingMeasurement={currentMeasurement}
          isAddMode={isAddMode}
          onClose={handleCloseFieldMeasurementModal}
          onSave={handleSaveMeasurement}
          wellsActions={wellsActions}
        />
      )}

      {/* Confirmation Modal for deletion */}
      {isFieldMeasurementConfirmationModalOpen && (
        <ConfirmationModal
          title="Delete Measurement"
          message="Are you sure you want to delete this field measurement? This action cannot be undone."
          onCancel={handleCloseDeleteConfirmationModal}
          onConfirm={handleConfirmDelete}
          confirmText="Delete"
          cancelText="Cancel"
          styles={{
            confirmationModalOverlay: styles.confirmationModalOverlay,
            confirmationModalContent: styles.confirmationModalContent,
            confirmationModalHeader: styles.confirmationModalHeader,
            confirmationModalMessage: styles.confirmationModalMessage,
            confirmationModalButtons: styles.confirmationModalButtons,
            confirmCancel: styles.confirmCancel,
            confirmButton: styles.confirmButton,
          }}
          confirmButtonClass="confirmButton"
        />
      )}

      {/* Sampling Start Modal */}
      <SamplingStartModal
        isOpen={showSamplingStartModal}
        onClose={() => setShowSamplingStartModal(false)}
      />
    </div>
  );
};
